import {useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {KTSVG} from '../../../../../../_metronic/helpers'

export const SUMMARY_BLOCK_IDS = [
  'project_info',
  'financial',
  'indicators',
  'extra_costs',
  'cycles',
  'milestones',
] as const

export type SummaryBlockId = (typeof SUMMARY_BLOCK_IDS)[number]

const BLOCK_LABELS: Record<SummaryBlockId, string> = {
  project_info: 'Dados do projeto',
  financial: 'Dados financeiros',
  indicators: 'Indicadores',
  extra_costs: 'Custos extras',
  cycles: 'Ciclos (resumo)',
  milestones: 'Marcos do projeto',
}

export type ProjectSummaryExportOptions = {
  include_blocks: SummaryBlockId[]
  include_private_milestones: boolean
}

type Props = {
  show: boolean
  onHide: () => void
  projectId: number
  projectType: number
  onExport: (options: ProjectSummaryExportOptions) => void
}

const modalsRoot = document.getElementById('root-modals') || document.body

const ProjectSummaryExportModal = ({show, onHide, projectId, projectType, onExport}: Props) => {
  const [includeBlocks, setIncludeBlocks] = useState<SummaryBlockId[]>([])
  const [includePrivateMilestones, setIncludePrivateMilestones] = useState(false)

  useEffect(() => {
    if (!show) return
    const defaultBlocks: SummaryBlockId[] = [
      'project_info',
      'financial',
      'indicators',
      'extra_costs',
      'milestones',
    ]
    if (projectType === 0) {
      defaultBlocks.push('cycles')
    }
    setIncludeBlocks(defaultBlocks)
    setIncludePrivateMilestones(false)
  }, [show, projectType])

  const toggleBlock = (id: SummaryBlockId) => {
    setIncludeBlocks((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]
    )
  }

  const handleExport = () => {
    onExport({
      include_blocks: includeBlocks,
      include_private_milestones: includePrivateMilestones,
    })
    onHide()
  }

  const availableBlocks: SummaryBlockId[] = projectType === 0
    ? [...SUMMARY_BLOCK_IDS]
    : SUMMARY_BLOCK_IDS.filter((id) => id !== 'cycles')

  return createPortal(
    <Modal
      id='kt_modal_project_summary_export'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-500px'
      show={show}
      onHide={onHide}
      data-backdrop='static'
    >
      <div className='modal-header'>
        <h2>Exportar resumo do projeto</h2>
        <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={onHide}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>
      <div className='modal-body py-lg-10 px-lg-10'>
        <p className='text-gray-600 mb-5'>Selecione os blocos que deseja incluir no PDF:</p>
        <div className='d-flex flex-column gap-4'>
          {availableBlocks.map((id) => (
            <label
              key={id}
              className='form-check form-check-custom form-check-solid align-items-center cursor-pointer'
            >
              <input
                type='checkbox'
                className='form-check-input h-20px w-20px me-3'
                checked={includeBlocks.includes(id)}
                onChange={() => toggleBlock(id)}
              />
              <span className='form-check-label fw-semibold text-gray-700'>
                {BLOCK_LABELS[id]}
              </span>
            </label>
          ))}
        </div>
        {availableBlocks.includes('milestones') && (
          <div className='mt-6 pt-5 border-top border-gray-200'>
            <label className='form-check form-check-custom form-check-solid align-items-center cursor-pointer'>
              <input
                type='checkbox'
                className='form-check-input h-20px w-20px me-3'
                checked={includePrivateMilestones}
                onChange={(e) => setIncludePrivateMilestones(e.target.checked)}
                disabled={!includeBlocks.includes('milestones')}
              />
              <span className='form-check-label fw-semibold text-gray-700'>
                Incluir marcos privados
              </span>
            </label>
          </div>
        )}
        <div className='d-flex justify-content-end gap-3 mt-8'>
          <button type='button' className='btn btn-light' onClick={onHide}>
            Cancelar
          </button>
          <button
            type='button'
            className='btn btn-danger'
            onClick={handleExport}
            disabled={includeBlocks.length === 0}
          >
            Exportar PDF
          </button>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

export {ProjectSummaryExportModal}
